import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ApiClientError,
  DecorKindSchema,
  extractLab,
  type ApiClientErrorCode,
  type DecorKind,
  type DecorMatchRequest,
  type DecorMatchResponseItem,
  type LabColor,
  type Region,
} from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { downscaleImageToImageData } from '@/components/PhotoTools/imageDownscale';
import type { PdfImageDoc } from '@/components/PhotoTools/pdfToImage';
import { PhotoPickerView, type PhotoPickerState } from './PhotoPickerView';
import { usePhotoMatchStore } from './photoMatchStore';
import { useDecorImageUrls } from './useDecorImageUrls';
import { decorFilterToQuery } from './decorFilterParams';

/**
 * Container PhotoPicker (Экран 2.2.3). См. ADR-0005, ADR-0008, ADR-0009, ADR-0042.
 *
 * **Единственный legitimate consumer of `extractLab`** в production-коде —
 * см. ESLint allowlist в `eslint.config.js`. P-009 защита 4-уровневая:
 * (1) `extractLab(ImageData, Region)` принимает только `ImageData`,
 * (2) `DecorMatchRequestSchema.strict()` блокирует extra поля,
 * (3) ESLint allowlist изолирует impotrt этого файла,
 * (4) E2E network snapshot (Phase 9 — `decors-p009-network-check.spec.ts`).
 *
 * Pipeline: File → URL.createObjectURL → <img>.decode() → downscaleImageToImageData
 * (≤1024×1024 для OOM safety) → extractLab(ImageData, Region) → POST с {l, a, b}.
 * **Фото не уезжает** — только числа.
 *
 * PR-W2 (ADR-0042): PDF принимается тоже — выбранная страница рендерится в canvas
 * НА КЛИЕНТЕ (`pdfToImage`, lazy-load) → `HTMLImageElement` → тот же `commitImage`
 * → тот же `runMatch`.
 *
 * PR-FLOW (ADR-0043): подбор ищет ВНУТРИ фильтра. Фильтры (subcategory/brand/type)
 * приходят из URL (`?subcategories=..&brands=..&types=..`, проброшены метод-пикером)
 * и добавляются в тело match ТОЛЬКО когда непустые. Без фильтра тело остаётся
 * ровно `{kind, mode, l, a, b}` (P-009 не нарушен — фильтры это каталожные строки,
 * не ПДн/фото; `.strict()` блокирует прочее). Фильтр-параметры — persistent intent:
 * при манипуляции transient-сигналом `?step` они СОХРАНЯЮТСЯ (см. buildStepParams).
 */
const DEFAULT_RADIUS_PERCENT = 10;

/** PDF по MIME-типу или расширению (input accept не гарантирует MIME). */
function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

export function PhotoPicker() {
  const navigate = useNavigate();
  const params = useParams<{ kind: string }>();
  const kindParse = DecorKindSchema.safeParse(params.kind);
  const kind: DecorKind = kindParse.success ? kindParse.data : 'facade';

  // PR-W2-FIX замечание 2: под-шаг «результаты» отражается в URL (`?step=results`,
  // push в историю), чтобы «Назад» шёл по шагам фото-экрана, а возврат из карточки
  // декора восстанавливал список результатов (стор переживает ремаунт).
  const [searchParams, setSearchParams] = useSearchParams();
  const stepParam = searchParams.get('step');

  // PR-FLOW (ADR-0043): фильтры из URL (проброшены метод-пикером). persistent
  // intent — попадают в match-запрос И сохраняются при манипуляции `?step`.
  const filterParams = useMemo(
    () => ({
      subcategories: searchParams.getAll('subcategories'),
      brands: searchParams.getAll('brands'),
      types: searchParams.getAll('types'),
    }),
    [searchParams],
  );
  // URLSearchParams с фильтрами (+опц. step). Используется вместо «голого»
  // setSearchParams({step}) / setSearchParams({}), которые затёрли бы фильтры.
  const buildStepParams = (step?: 'results'): URLSearchParams => {
    const p = new URLSearchParams();
    for (const v of filterParams.subcategories) p.append('subcategories', v);
    for (const v of filterParams.brands) p.append('brands', v);
    for (const v of filterParams.types) p.append('types', v);
    if (step) p.set('step', step);
    return p;
  };
  const setStoreImage = usePhotoMatchStore((s) => s.setImage);
  const setStoreResults = usePhotoMatchStore((s) => s.setResults);
  const clearStore = usePhotoMatchStore((s) => s.clear);
  const storeKind = usePhotoMatchStore((s) => s.kind);
  const storeItems = usePhotoMatchStore((s) => s.matchItems);
  const storeImageEl = usePhotoMatchStore((s) => s.imageEl);
  const storeImageUrl = usePhotoMatchStore((s) => s.imageUrl);
  const storeImageWidth = usePhotoMatchStore((s) => s.imageWidth);
  const storeImageHeight = usePhotoMatchStore((s) => s.imageHeight);

  const [state, setState] = useState<PhotoPickerState>('noImage');
  const [imageEl, setImageEl] = useState<HTMLImageElement | null>(null);
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [cx, setCx] = useState(0);
  const [cy, setCy] = useState(0);
  const [radiusPercent, setRadiusPercent] = useState(DEFAULT_RADIUS_PERCENT);
  const [matchItems, setMatchItems] = useState<readonly DecorMatchResponseItem[] | undefined>(
    undefined,
  );
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | undefined>(
    undefined,
  );
  // Фото-2 (PR-DATA4): signed-URL для фото результатов подбора.
  const matchPhotoKeys = useMemo(
    () => (matchItems ?? []).map((i) => i.primaryPhotoKey),
    [matchItems],
  );
  const { urls: matchImageUrls } = useDecorImageUrls(matchPhotoKeys);
  // Многостраничный PDF: текущая страница (1-based) + всего страниц. numPages=0
  // когда PDF не открыт. Сам handle документа — в ref (не для рендера).
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfNumPages, setPdfNumPages] = useState(0);

  // Все ObjectURL'ы которые мы создали. Без этого set'а URL созданный в
  // onFileSelect до setImageUrl() (т.е. unmount во время `await img.decode()`)
  // утекает: cleanup-effect видит только `imageUrl` из state, а не in-flight `url`.
  const createdUrlsRef = useRef<Set<string>>(new Set());
  // Каждый вызов onFileSelect получает уникальный sessionId. Если пока
  // decode/PDF-рендер шёл, юзер выбрал другой файл — старый sessionId становится
  // stale и его result отбрасывается (race на быстром re-selection).
  const uploadIdRef = useRef(0);
  // Открытый PDF-документ (lazy `pdfToImage`). Держим для перерисовки страниц
  // в степпере; destroy() на reset/unmount/новый файл.
  const pdfDocRef = useRef<PdfImageDoc | null>(null);
  // Токен race-guard для смены страницы PDF (быстрые клики ‹/›).
  const pdfRenderIdRef = useRef(0);

  // radius в image-pixel space, derived from radiusPercent.
  const minSide = imageWidth > 0 && imageHeight > 0 ? Math.min(imageWidth, imageHeight) : 0;
  const radius = Math.max(1, Math.round((minSide * radiusPercent) / 100));

  // Cleanup на unmount: revoke любые URL + destroy PDF-документа.
  useEffect(() => {
    const created = createdUrlsRef.current;
    const pdfRef = pdfDocRef;
    return () => {
      for (const u of created) URL.revokeObjectURL(u);
      created.clear();
      pdfRef.current?.destroy();
      pdfRef.current = null;
    };
  }, []);

  // Реконсиляция внутреннего шага с URL-параметром `step` (back/forward/ремаунт).
  // `step` — transient signal (R3): без стора (reload/закладка) деградирует штатно
  // в noImage, параметр чистится через replace.
  useEffect(() => {
    if (stepParam === 'results') {
      if (state !== 'results') {
        if (storeKind === kind && storeItems && storeImageEl && storeImageUrl) {
          // Возврат из карточки (ремаунт) / forward → восстановить ВСЮ сессию
          // (фото + результаты), чтобы дальнейший «Назад» вёл на imageLoaded с фото.
          setImageEl(storeImageEl);
          setImageUrl(storeImageUrl);
          setImageWidth(storeImageWidth);
          setImageHeight(storeImageHeight);
          setCx(Math.round(storeImageWidth / 2));
          setCy(Math.round(storeImageHeight / 2));
          setRadiusPercent(DEFAULT_RADIUS_PERCENT);
          setMatchItems(storeItems);
          setState('results');
        } else {
          // Пустой стор (reload/закладка) или другой kind → убрать transient
          // `step`, СОХРАНИВ фильтры (persistent intent, ADR-0043).
          setSearchParams(buildStepParams(), { replace: true });
        }
      }
    } else if (state === 'results') {
      // «Назад» из результатов → к выбору режима с фото (imageEl восстановлен из
      // стора при ремаунте) либо noImage, если фото действительно нет.
      const hasImage = imageEl ?? storeImageEl;
      setState(hasImage ? 'imageLoaded' : 'noImage');
    }
    // Эффект намеренно реагирует только на смену `stepParam` (как route-param sync).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepParam]);

  function revokeAndUntrack(url: string) {
    URL.revokeObjectURL(url);
    createdUrlsRef.current.delete(url);
  }

  function destroyPdf() {
    pdfDocRef.current?.destroy();
    pdfDocRef.current = null;
    setPdfNumPages(0);
    setPdfPage(1);
  }

  // Общий «commit» изображения (растр ИЛИ страница PDF) → штатный imageLoaded.
  // `displayUrl` — то, что идёт в <img src> (objectURL для растра, data-URL для PDF).
  function commitImage(img: HTMLImageElement, displayUrl: string) {
    setImageEl(img);
    setImageUrl(displayUrl);
    setImageWidth(img.naturalWidth);
    setImageHeight(img.naturalHeight);
    setCx(Math.round(img.naturalWidth / 2));
    setCy(Math.round(img.naturalHeight / 2));
    setRadiusPercent(DEFAULT_RADIUS_PERCENT);
    setState('imageLoaded');
    // Сохраняем фото в session-стор (переживёт ремаунт при заходе в карточку).
    // Передаём владение url стору: убираем из createdUrlsRef, чтобы unmount-
    // cleanup его НЕ revoke'ил, пока фото в сторе. revoke — в store.setImage
    // (замена) / store.clear() (сброс).
    setStoreImage({
      imageEl: img,
      imageUrl: displayUrl,
      imageWidth: img.naturalWidth,
      imageHeight: img.naturalHeight,
      kind,
    });
    createdUrlsRef.current.delete(displayUrl);
  }

  async function onFileSelect(file: File) {
    // Прежний committed-url принадлежит стору и будет revoke'нут при коммите
    // нового фото (`store.setImage`) — здесь его НЕ трогаем (фото может ещё
    // понадобиться, если новый файл не декодируется).
    destroyPdf();
    setError(undefined);
    const sessionId = ++uploadIdRef.current;

    if (isPdfFile(file)) {
      await handlePdfFile(file, sessionId);
      return;
    }

    // Растровый путь (JPG/PNG) — без изменений с Phase 7.
    const url = URL.createObjectURL(file);
    createdUrlsRef.current.add(url);
    const img = new Image();
    img.src = url;
    try {
      await img.decode();
      // Race guard: пока этот decode шёл, юзер выбрал другой файл — отбрасываем.
      if (sessionId !== uploadIdRef.current) {
        revokeAndUntrack(url);
        return;
      }
      commitImage(img, url);
    } catch {
      revokeAndUntrack(url);
      if (sessionId !== uploadIdRef.current) return;
      setError({ code: 'INTERNAL_ERROR', message: 'Не удалось прочитать фото' });
      setState('error');
    }
  }

  async function handlePdfFile(file: File, sessionId: number) {
    setState('loading');
    try {
      // Lazy-load pdfjs ТОЛЬКО при выборе PDF (ADR-0042) — отдельный чанк.
      const { loadPdf } = await import('@/components/PhotoTools/pdfToImage');
      const doc = await loadPdf(file);
      if (sessionId !== uploadIdRef.current) {
        doc.destroy();
        return;
      }
      const img = await doc.renderPage(1);
      if (sessionId !== uploadIdRef.current) {
        doc.destroy();
        return;
      }
      pdfDocRef.current = doc;
      if (doc.numPages === 1) {
        // Одностраничный PDF — сразу в штатный imageLoaded (без степпера).
        commitImage(img, img.src);
      } else {
        // Многостраничный — экран выбора страницы (data-URL превью в imageUrl).
        setImageEl(img);
        setImageUrl(img.src);
        setImageWidth(img.naturalWidth);
        setImageHeight(img.naturalHeight);
        setPdfNumPages(doc.numPages);
        setPdfPage(1);
        setState('pdfPageSelect');
      }
    } catch {
      if (sessionId !== uploadIdRef.current) return;
      destroyPdf();
      setError({ code: 'INTERNAL_ERROR', message: 'Не удалось открыть PDF' });
      setState('error');
    }
  }

  async function onPdfChangePage(nextPage: number) {
    const doc = pdfDocRef.current;
    if (!doc || nextPage < 1 || nextPage > doc.numPages) return;
    const renderId = ++pdfRenderIdRef.current;
    try {
      const img = await doc.renderPage(nextPage);
      // Race guard: пока рендер шёл, юзер кликнул ещё раз / сбросил.
      if (renderId !== pdfRenderIdRef.current) return;
      setImageEl(img);
      setImageUrl(img.src);
      setImageWidth(img.naturalWidth);
      setImageHeight(img.naturalHeight);
      setPdfPage(nextPage);
    } catch {
      if (renderId !== pdfRenderIdRef.current) return;
      setError({ code: 'INTERNAL_ERROR', message: 'Не удалось отрисовать страницу' });
      setState('error');
    }
  }

  function onPdfSelectPage() {
    // Текущая страница (imageEl/imageUrl уже отрендерены) → штатный imageLoaded.
    if (!imageEl || !imageUrl) return;
    commitImage(imageEl, imageUrl);
  }

  async function runMatch(mode: 'area' | 'average') {
    if (!imageEl) return;
    setState('loading');
    setError(undefined);
    try {
      // Pre-shrink до MAX_DOWNSCALE_SIZE — защита от OOM на iPhone 12+ (50 МП).
      const { imageData, scale } = downscaleImageToImageData(imageEl);
      // Image-pixel space → downscaled coords. ADR-0008 §1: круг хранится
      // в image-pixel space; для extractLab переводим в downscaled space.
      const region: Region =
        mode === 'area'
          ? { type: 'circle', cx: cx * scale, cy: cy * scale, r: radius * scale }
          : { type: 'average' };
      const lab: LabColor = extractLab(imageData, region);
      // P-009 — никаких photo, blob, URL, mask: только числа LAB + опц. каталожные
      // фильтры-строки. DecorMatchRequestSchema.strict() добавляет runtime-страховку.
      // PR-FLOW (ADR-0043): фильтры включаем ТОЛЬКО когда непустые → подбор без
      // фильтра шлёт ровно {kind, mode, l, a, b} (P-009 body-shape unchanged).
      const request: DecorMatchRequest = {
        kind,
        mode,
        l: lab.l,
        a: lab.a,
        b: lab.b,
        ...(filterParams.subcategories.length > 0
          ? { subcategories: filterParams.subcategories }
          : {}),
        ...(filterParams.brands.length > 0 ? { brands: filterParams.brands } : {}),
        ...(filterParams.types.length > 0 ? { types: filterParams.types } : {}),
      };
      const response = await apiClient.decors.match(request);
      setMatchItems(response.items);
      // Сохраняем результаты в session-стор (фото уже в сторе из commitImage) +
      // пушим под-шаг в историю (чтобы «Назад» вернул к выбору режима).
      setStoreResults(response.items);
      setState('results');
      // step=results (transient) + сохранённые фильтры (persistent, ADR-0043).
      setSearchParams(buildStepParams('results'));
    } catch (e: unknown) {
      // Не leak-аем String(err) в UI: для non-ApiClientError используем
      // фиксированный текст в Presenter (см. ErrorState). message='' →
      // Presenter не рендерит технический detail-block.
      const apiErr =
        e instanceof ApiClientError
          ? { code: e.code, message: e.message }
          : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
      setError(apiErr);
      setState('error');
    }
  }

  function onResetImage() {
    // Бампим session id — любой in-flight decode/PDF-рендер становится stale.
    ++uploadIdRef.current;
    ++pdfRenderIdRef.current;
    destroyPdf();
    // Реальный сброс — revoke фото (store-owned url) + любые pending url.
    clearStore();
    for (const u of createdUrlsRef.current) URL.revokeObjectURL(u);
    createdUrlsRef.current.clear();
    // Сбрасываем transient `?step` (если были на результатах) — иначе reconcile
    // вернёт в results по пустому стору. Фильтры СОХРАНЯЕМ (та же scope-сессия).
    if (stepParam) setSearchParams(buildStepParams(), { replace: true });
    setState('noImage');
    setImageEl(null);
    setImageUrl(undefined);
    setImageWidth(0);
    setImageHeight(0);
    setMatchItems(undefined);
    setError(undefined);
  }

  return (
    <PhotoPickerView
      kind={kind}
      state={state}
      imageUrl={imageUrl}
      imageWidth={imageWidth}
      imageHeight={imageHeight}
      cx={cx}
      cy={cy}
      radius={radius}
      radiusPercent={radiusPercent}
      pdfPage={pdfPage}
      pdfNumPages={pdfNumPages}
      matchItems={matchItems}
      matchImageUrls={matchImageUrls}
      error={error}
      onFileSelect={onFileSelect}
      onAverageMatch={() => runMatch('average')}
      onActivatePicker={() => setState('pickerActive')}
      onAreaMatch={() => runMatch('area')}
      onBackToImageLoaded={() => setState('imageLoaded')}
      onCircleMove={(newCx, newCy) => {
        setCx(newCx);
        setCy(newCy);
      }}
      onRadiusChange={setRadiusPercent}
      onPdfPrevPage={() => onPdfChangePage(pdfPage - 1)}
      onPdfNextPage={() => onPdfChangePage(pdfPage + 1)}
      onPdfSelectPage={onPdfSelectPage}
      onResetImage={onResetImage}
      onItemClick={(globalId) => {
        // ADR-0055: несём активный фильтр подбора в URL карточки → в альтернативы.
        const qs = decorFilterToQuery(filterParams);
        navigate(
          `/decors/${encodeURIComponent(kind)}/${encodeURIComponent(globalId)}${qs ? `?${qs}` : ''}`,
        );
      }}
    />
  );
}
